import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LockIcon, EyeIcon, EyeOffIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
interface ResetPasswordProps {
  navigate: (screen: string) => void;
}
export function ResetPassword({
  navigate
}: ResetPasswordProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleReset = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) return;
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setIsSuccess(true);
      setTimeout(() => {
        navigate('login');
      }, 2000);
    }, 1500);
  };
  if (isSuccess) {
    return <div className="flex-1 bg-[#0D3B3B] flex flex-col items-center justify-center h-full px-6" data-id="element-2709">
        <motion.div initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} className="bg-white p-8 rounded-[2rem] flex flex-col items-center text-center max-w-sm w-full shadow-2xl" data-id="element-2710">
          <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mb-6" data-id="element-2711">
            <CheckCircleIcon className="w-10 h-10 text-emerald-500" data-id="element-2712" />
          </div>
          <h2 className="text-2xl font-black text-[#0D3B3B] mb-2" data-id="element-2713">
            Password Reset!
          </h2>
          <p className="text-gray-500 font-medium text-sm mb-6" data-id="element-2714">
            Your password has been successfully updated. Redirecting to login...
          </p>
          <div className="w-6 h-6 border-2 border-[#0D3B3B]/20 border-t-[#0D3B3B] rounded-full animate-spin" data-id="element-2715" />
        </motion.div>
      </div>;
  }
  return <div className="flex-1 bg-slate-50 flex flex-col h-full relative overflow-hidden" data-id="element-2716">
      {/* Header Background */}
      <div className="absolute top-0 left-0 right-0 h-80 bg-[#FFD700] rounded-b-[3rem]" data-id="element-2717" />

      {/* Content */}
      <div className="flex-1 flex flex-col px-6 pt-12 pb-safe z-10 overflow-y-auto" data-id="element-2718">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="flex flex-col items-center mb-10" data-id="element-2719">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg mb-6" data-id="element-2720">
            <LockIcon className="w-8 h-8 text-[#0D3B3B]" data-id="element-2721" />
          </div>
          <h1 className="text-3xl font-black text-[#0D3B3B] mb-2 text-center" data-id="element-2722">
            New Password
          </h1>
          <p className="text-[#0D3B3B]/70 text-sm text-center max-w-[260px] font-medium" data-id="element-2723">
            Create a new secure password for your account.
          </p>
        </motion.div>

        {/* Form */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="bg-white rounded-[2rem] p-6 shadow-xl shadow-[#0D3B3B]/5 flex flex-col max-w-md w-full mx-auto" data-id="element-2724">
          <form onSubmit={handleReset} className="flex flex-col" data-id="element-2725">
            <div className="space-y-4 mb-8" data-id="element-2726">
              {/* New Password */}
              <div className="space-y-1.5" data-id="element-2727">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2728">
                  New Password
                </label>
                <div className="relative flex items-center" data-id="element-2729">
                  <div className="absolute left-4 text-gray-400" data-id="element-2730">
                    <LockIcon className="w-5 h-5" data-id="element-2731" />
                  </div>
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter new password" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-4 pl-12 pr-12 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2732" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2733">
                    {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-2734" /> : <EyeIcon className="w-5 h-5" data-id="element-2735" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-1.5" data-id="element-2736">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2737">
                  Confirm Password
                </label>
                <div className="relative flex items-center" data-id="element-2738">
                  <div className="absolute left-4 text-gray-400" data-id="element-2739">
                    <LockIcon className="w-5 h-5" data-id="element-2740" />
                  </div>
                  <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-4 pl-12 pr-12 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2741" />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2742">
                    {showConfirmPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-2743" /> : <EyeIcon className="w-5 h-5" data-id="element-2744" />}
                  </button>
                </div>
                {password && confirmPassword && password !== confirmPassword && <p className="text-xs text-rose-500 font-bold ml-1 mt-1" data-id="element-2745">
                      Passwords do not match
                    </p>}
              </div>
            </div>

            <button type="submit" disabled={isLoading || !password || !confirmPassword || password !== confirmPassword} className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-95 ${isLoading || !password || !confirmPassword || password !== confirmPassword ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#0D3B3B] text-white hover:bg-[#092D2D] shadow-lg shadow-[#0D3B3B]/20'}`} data-id="element-2746">
              {isLoading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2747" /> : <>
                  Reset Password
                  <ArrowRightIcon className="w-5 h-5" data-id="element-2748" />
                </>}
            </button>
          </form>
        </motion.div>
      </div>
    </div>;
}